/**
 * PPR-3 F · 同行详情页「同行站动态」三块：这个同行的订单（可按同行单号搜）· 未处理事件 · 最近登录。
 * 订单：现有订单列表接口（channel_partner_id + search，search 同时搜客户 / 订单号 / 平台订单号 = 同行单号）；
 * 事件：GET /api/v1/channel-partners/{id}/partner-events?status=open（PPR-3 聚合只读，点进订单详情回复）；
 * 最近登录：现有同行账号接口的 last_login_at（账号接口需要「账号管理」权限，没有权限显示提示）。
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Can, useCan } from "@/auth/permissions";
import { partnerAccountsApi } from "@/services/b2b";
import { listOrders } from "@/services/orders";
import { PE_QK, getPartnerEvents } from "@/services/partnerEvents";
import { PartnerEventRows } from "./PartnerEventRows";

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("zh-CN", { hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "从未登录");

function Card({ title, sub, children, testid }: { title: React.ReactNode; sub?: string; children: React.ReactNode; testid: string }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3"><div className="text-[14px] font-semibold text-slate-900">{title}</div>{sub && <div className="text-[11px] text-slate-500">{sub}</div>}</div>
      {children}
    </section>
  );
}

function PartnerOrders({ partnerId }: { partnerId: string }) {
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const q = useQuery({ queryKey: ["b2b", "partner-orders", partnerId, search], queryFn: () => listOrders({ channel_partner_id: partnerId, search: search || undefined, page_size: 10, sort_by: "travel_date", sort_dir: "desc" }, { paged: true }) });
  const rows = q.data ?? [];
  return (
    <Card testid="partner-orders-card" title="这个同行的订单" sub="最近 10 单（按出发日倒序）· 可按同行单号 / 订单号 / 客人姓名搜">
      <form className="mb-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); setSearch(text.trim()); }}>
        <input aria-label="搜同行单号" value={text} onChange={(e) => setText(e.target.value)} placeholder="同行单号 / 订单号 / 客人"
          className="h-8 flex-1 rounded-md border border-slate-300 px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button type="submit" className="rounded-md border border-slate-300 px-3 text-[12.5px] text-slate-600 hover:bg-slate-50">搜索</button>
      </form>
      {q.isLoading ? <p className="text-[12.5px] text-slate-400">加载中…</p> : q.isError ? (
        <button type="button" onClick={() => q.refetch()} className="text-[12.5px] text-rose-600 underline">加载失败，点击重试</button>
      ) : rows.length === 0 ? <p className="text-[12.5px] text-slate-400">{search ? "没有符合的订单" : "这个同行还没有订单"}</p> : (
        <table className="w-full text-[12.5px]">
          <thead><tr className="text-left text-[11px] text-slate-400"><th className="py-1">同行单号</th><th>订单号</th><th>状态</th><th>出发日</th><th className="text-right">金额</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} data-testid="partner-order-row" className="border-t border-slate-100">
                <td className="py-1.5">{o.channel_external_order_id ?? <span className="text-slate-300">—</span>}</td>
                <td><Link to={`/orders/${o.id}`} className="font-medium text-blue-600 hover:underline">{o.order_no}</Link></td>
                <td>{o.status}</td><td>{o.travel_date ?? "—"}</td>
                <td className="text-right tabular-nums">${(o.total_amount_cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function OpenEvents({ partnerId }: { partnerId: string }) {
  const q = useQuery({ queryKey: PE_QK.partner(partnerId), queryFn: () => getPartnerEvents(partnerId, "open") });
  return (
    <Card testid="partner-events-open-card" title={<>未处理事件{q.data && q.data.open_count > 0 && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{q.data.open_count}</span>}</>}
      sub="同行在同行站提的需求 · 点订单号进订单详情回复">
      {q.isLoading ? <p className="text-[12.5px] text-slate-400">加载中…</p> : q.isError ? (
        <button type="button" onClick={() => q.refetch()} className="text-[12.5px] text-rose-600 underline">加载失败，点击重试</button>
      ) : <PartnerEventRows rows={q.data?.items ?? []} empty="没有未处理的事件" showPartner={false} />}
    </Card>
  );
}

function LastLogin({ partnerId }: { partnerId: string }) {
  const can = useCan("channel_partners.accounts.manage");
  const q = useQuery({ queryKey: ["b2b", "partner-accounts-login", partnerId], queryFn: () => partnerAccountsApi.list(partnerId), enabled: can });
  return (
    <Card testid="partner-last-login-card" title="最近登录" sub="同行站登录账号的最近登录时间">
      {!can ? <p className="text-[12.5px] text-slate-400">查看账号需要「账号管理」权限</p> : q.isLoading ? <p className="text-[12.5px] text-slate-400">加载中…</p> : q.isError ? (
        <button type="button" onClick={() => q.refetch()} className="text-[12.5px] text-rose-600 underline">加载失败，点击重试</button>
      ) : (q.data ?? []).length === 0 ? <p className="text-[12.5px] text-slate-400">还没有同行账号</p> : (
        <ul className="flex flex-col gap-1.5 text-[12.5px]">
          {(q.data ?? []).map((a) => (
            <li key={a.id} data-testid="partner-login-row" className="flex items-center justify-between gap-2">
              <span className="truncate">{a.full_name} <span className="text-slate-400">· {a.role === "primary" ? "主账号" : "副账号"}{a.status !== "active" ? ` · ${a.status === "invited" ? "未激活" : "已停用"}` : ""}</span></span>
              <span className="shrink-0 tabular-nums text-slate-600">{fmt(a.last_login_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function PartnerSiteActivity({ partnerId }: { partnerId: string }) {
  return (
    <div data-testid="partner-site-activity" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Can perm="orders.orders.list"><PartnerOrders partnerId={partnerId} /></Can>
      <Can perm="orders.orders.list"><OpenEvents partnerId={partnerId} /></Can>
      <LastLogin partnerId={partnerId} />
    </div>
  );
}
